import { Fragment, useEffect, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api-client';
import { cn } from '../lib/cn';

/**
 * Audit Log Explorer (M1.8.b)
 *
 * Exactly one primary filter — actor | tenant | action — per request, per the
 * DynamoDB schema (see lambda/admin/audit-query/index.js).
 *   - actor  → GSI ByActor, day-fanout across range
 *   - tenant → main table,  day-fanout across range
 *   - action → GSI ByAction, single Query with SK range (fastest)
 */

type Mode = 'actor' | 'tenant' | 'action';

type AuditRow = {
  auditId: string;
  timestamp: string;
  actorId: string;
  actorEmail: string;
  actorRole: string;
  targetType: string;
  targetId: string;
  action: string;
  reason: string | null;
  success: boolean;
  error: string | null;
  before: unknown;
  after: unknown;
  ipAddress: string | null;
  userAgent: string | null;
  requestId: string | null;
};

type AuditResponse = {
  rows: AuditRow[];
  nextCursor: string | null;
  count: number;
  queriedDays: number | null;
  truncated: boolean;
  range: { from: string; to: string };
};

type Query = {
  mode: Mode;
  value: string;
  from: string;
  to: string;
  limit: number;
};

type AdminOption = { adminId: string | null; email: string | null; role: string | null; username: string };
type TenantOption = { tenantId: string; businessName: string | null; accountNumber: string | null };

/**
 * Known audited actions — hardcoded against the Lambdas currently writing audit rows.
 * Extend this list as Phase 2 actions ship (subscription.change-plan, credits.grant,
 * refund.issue, tenant.impersonate-start, etc. per DESIGN-support-actions-catalog.md).
 */
const KNOWN_ACTIONS: { value: string; label: string }[] = [
  { value: 'admin.create', label: 'admin.create — new admin invited' },
  { value: 'admin.update', label: 'admin.update — role/status changed' },
  { value: 'admin.reset-mfa', label: 'admin.reset-mfa — MFA re-enrollment forced' },
  { value: 'tenant.read-transcript', label: 'tenant.read-transcript — call transcript viewed (PII)' },
];

function defaultRange(): { from: string; to: string } {
  const today = new Date();
  const from = new Date(today);
  from.setUTCDate(from.getUTCDate() - 6);
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  return { from: fmt(from), to: fmt(today) };
}

export function AuditPage() {
  const [searchParams] = useSearchParams();
  const [submitted, setSubmitted] = useState<Query | null>(null);

  // Honor deep-links like /audit?tenant=X or /audit?actor=Y or /audit?action=Z —
  // used by the UCR "Open full log →" link. We compute this once on mount.
  const [initialQuery] = useState<Query | null>(() => {
    for (const mode of ['actor', 'tenant', 'action'] as const) {
      const v = searchParams.get(mode)?.trim();
      if (v) {
        const { from, to } = defaultRange();
        return {
          mode,
          value: v,
          from: searchParams.get('from') || from,
          to: searchParams.get('to') || to,
          limit: Number(searchParams.get('limit')) || 50,
        };
      }
    }
    return null;
  });

  useEffect(() => {
    if (initialQuery) setSubmitted(initialQuery);
  }, [initialQuery]);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-semibold">Audit Log</h1>
        <p className="text-sm text-muted">Every admin action, queryable by actor / tenant / action / time.</p>
      </div>

      <FilterBar onSubmit={setSubmitted} initial={initialQuery} />

      {submitted && <Results query={submitted} />}
    </div>
  );
}

function FilterBar({ onSubmit, initial }: { onSubmit: (q: Query) => void; initial: Query | null }) {
  const [mode, setMode] = useState<Mode>(initial?.mode ?? 'actor');
  const [value, setValue] = useState(initial?.value ?? '');
  const range = defaultRange();
  const [from, setFrom] = useState(initial?.from ?? range.from);
  const [to, setTo] = useState(initial?.to ?? range.to);
  const [limit, setLimit] = useState(initial?.limit ?? 50);

  // Reuse the same endpoints the Admins + Tenants pages consume — React Query
  // dedupes so navigating here after those pages is zero-network.
  const adminsQ = useQuery({
    queryKey: ['admins'],
    queryFn: () => api.get<{ admins: AdminOption[] }>('/admins'),
    enabled: mode === 'actor',
  });
  const tenantsQ = useQuery({
    queryKey: ['tenants', 'all-for-audit'],
    queryFn: () => api.get<{ tenants: TenantOption[] }>('/tenants?limit=200'),
    enabled: mode === 'tenant',
  });

  const listLoading =
    (mode === 'actor' && adminsQ.isLoading) ||
    (mode === 'tenant' && tenantsQ.isLoading);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!value.trim()) return;
    onSubmit({ mode, value: value.trim(), from, to, limit });
  };

  return (
    <form onSubmit={handleSubmit} className="panel p-4 space-y-3">
      <div className="flex gap-1 text-sm">
        {(['actor', 'tenant', 'action'] as const).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => { setMode(m); setValue(''); }}
            className={cn(
              'px-3 py-1.5 rounded border',
              mode === m
                ? 'bg-accent text-accent-fg border-accent'
                : 'border-border hover:bg-bg',
            )}
          >
            By {m}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
        <label className="md:col-span-2 block space-y-1">
          <span className="text-xs text-muted">
            {mode === 'actor' && 'Admin'}
            {mode === 'tenant' && 'Tenant'}
            {mode === 'action' && 'Action'}
          </span>
          {mode === 'actor' && (
            <select
              value={value}
              onChange={(e) => setValue(e.target.value)}
              required
              disabled={adminsQ.isLoading}
              className="w-full px-2 py-1.5 text-sm bg-bg border border-border rounded focus:border-accent outline-none"
            >
              <option value="">{adminsQ.isLoading ? 'Loading admins…' : 'Select an admin'}</option>
              {(adminsQ.data?.admins ?? [])
                .filter((a) => a.adminId)
                .map((a) => (
                  <option key={a.adminId!} value={a.adminId!}>
                    {(a.email ?? a.username)}{a.role ? ` (${a.role})` : ''}
                  </option>
                ))}
            </select>
          )}
          {mode === 'tenant' && (
            <select
              value={value}
              onChange={(e) => setValue(e.target.value)}
              required
              disabled={tenantsQ.isLoading}
              className="w-full px-2 py-1.5 text-sm bg-bg border border-border rounded focus:border-accent outline-none"
            >
              <option value="">{tenantsQ.isLoading ? 'Loading tenants…' : 'Select a tenant'}</option>
              {[...(tenantsQ.data?.tenants ?? [])]
                .sort((a, b) => (a.businessName ?? '').localeCompare(b.businessName ?? ''))
                .map((t) => (
                  <option key={t.tenantId} value={t.tenantId}>
                    {t.businessName ?? '(unnamed)'}{t.accountNumber ? ` · #${t.accountNumber}` : ''}
                  </option>
                ))}
            </select>
          )}
          {mode === 'action' && (
            <select
              value={value}
              onChange={(e) => setValue(e.target.value)}
              required
              className="w-full px-2 py-1.5 text-sm bg-bg border border-border rounded focus:border-accent outline-none"
            >
              <option value="">Select an action</option>
              {KNOWN_ACTIONS.map((a) => (
                <option key={a.value} value={a.value}>{a.label}</option>
              ))}
            </select>
          )}
        </label>
        <label className="block space-y-1">
          <span className="text-xs text-muted">From</span>
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="w-full px-2 py-1.5 text-sm bg-bg border border-border rounded focus:border-accent outline-none"
          />
        </label>
        <label className="block space-y-1">
          <span className="text-xs text-muted">To</span>
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="w-full px-2 py-1.5 text-sm bg-bg border border-border rounded focus:border-accent outline-none"
          />
        </label>
      </div>

      <div className="flex items-center gap-3">
        <label className="text-xs text-muted">
          Limit
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="ml-2 px-2 py-1 text-sm bg-bg border border-border rounded focus:border-accent outline-none"
          >
            {[50, 100, 200, 500].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <button
          type="submit"
          disabled={listLoading || !value}
          className="ml-auto bg-accent text-accent-fg text-sm font-medium px-3 py-1.5 rounded hover:opacity-95 disabled:opacity-60"
        >
          Search
        </button>
      </div>
    </form>
  );
}

function Results({ query }: { query: Query }) {
  const params = new URLSearchParams({
    [query.mode]: query.value,
    from: query.from,
    to: query.to,
    limit: String(query.limit),
  });

  const q = useQuery({
    queryKey: ['audit', query],
    queryFn: () => api.get<AuditResponse>(`/audit?${params.toString()}`),
  });

  if (q.isLoading) {
    return <div className="panel p-8 text-center text-sm text-muted">Querying audit log…</div>;
  }
  if (q.isError) {
    const e = q.error;
    const msg = e instanceof ApiError ? `${e.status} · ${e.code} · ${e.message}` : String(e);
    return (
      <div className="panel p-4 text-sm text-danger bg-danger/5 border-danger/30">
        <div className="font-medium">Query failed</div>
        <div className="text-xs mt-1 font-mono">{msg}</div>
      </div>
    );
  }
  if (!q.data) return null;
  if (q.data.rows.length === 0) {
    return (
      <div className="panel p-6 text-sm text-muted text-center">
        No audit rows for <code>{query.mode}={query.value}</code> between {query.from} and {query.to}.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="text-xs text-muted flex items-center gap-3">
        <span>{q.data.count} rows</span>
        {q.data.queriedDays !== null && <span>· scanned {q.data.queriedDays} day{q.data.queriedDays === 1 ? '' : 's'}</span>}
        {q.data.truncated && <span className="text-accent">· more available — narrow the date range or raise the limit</span>}
      </div>
      <AuditTable rows={q.data.rows} />
    </div>
  );
}

function AuditTable({ rows }: { rows: AuditRow[] }) {
  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <div className="panel overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-bg text-xs text-muted uppercase tracking-wide">
          <tr>
            <Th>Time</Th>
            <Th>Actor</Th>
            <Th>Action</Th>
            <Th>Target</Th>
            <Th>Outcome</Th>
            <Th>Reason</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((r) => (
            <Fragment key={r.auditId}>
              <tr
                className="hover:bg-bg/60 cursor-pointer"
                onClick={() => setExpanded(expanded === r.auditId ? null : r.auditId)}
              >
                <Td className="text-xs whitespace-nowrap">{formatTime(r.timestamp)}</Td>
                <Td>
                  <div className="text-xs font-medium text-fg">{r.actorEmail}</div>
                  <div className="text-xs text-muted">{r.actorRole}</div>
                </Td>
                <Td className="text-xs"><code>{r.action}</code></Td>
                <Td className="text-xs">
                  <span className="text-muted">{r.targetType}:</span>{' '}
                  <code className="font-mono">{r.targetId}</code>
                </Td>
                <Td><OutcomeBadge success={r.success} error={r.error} /></Td>
                <Td className="text-xs text-muted max-w-xs truncate">{r.reason ?? '—'}</Td>
              </tr>
              {expanded === r.auditId && <DetailRow row={r} />}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DetailRow({ row }: { row: AuditRow }) {
  return (
    <tr className="bg-bg/40">
      <td colSpan={6} className="px-3 py-3">
        <div className="grid grid-cols-2 gap-4 text-xs">
          <div>
            <div className="text-muted mb-1">Metadata</div>
            <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-0.5 font-mono">
              <dt className="text-muted">auditId</dt><dd>{row.auditId}</dd>
              <dt className="text-muted">actorId</dt><dd>{row.actorId}</dd>
              <dt className="text-muted">requestId</dt><dd>{row.requestId ?? '—'}</dd>
              <dt className="text-muted">ip</dt><dd>{row.ipAddress ?? '—'}</dd>
              <dt className="text-muted">ua</dt><dd className="truncate">{row.userAgent ?? '—'}</dd>
            </dl>
          </div>
          <div>
            <div className="text-muted mb-1">State</div>
            {row.before !== null && (
              <>
                <div className="text-muted">before</div>
                <pre className="bg-bg border border-border rounded p-2 overflow-auto max-h-40 font-mono text-[10px]">
                  {JSON.stringify(row.before, null, 2)}
                </pre>
              </>
            )}
            {row.after !== null && (
              <>
                <div className="text-muted mt-1">after</div>
                <pre className="bg-bg border border-border rounded p-2 overflow-auto max-h-40 font-mono text-[10px]">
                  {JSON.stringify(row.after, null, 2)}
                </pre>
              </>
            )}
            {row.before === null && row.after === null && <div className="text-muted">No state diff recorded.</div>}
            {row.error && (
              <div className="text-danger mt-2">
                <div className="text-muted">error</div>
                <div className="font-mono">{row.error}</div>
              </div>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
}

function OutcomeBadge({ success, error }: { success: boolean; error: string | null }) {
  if (success) {
    return <span className="inline-block text-xs px-1.5 py-0.5 rounded bg-accent/10 text-accent">ok</span>;
  }
  return (
    <span
      title={error ?? undefined}
      className="inline-block text-xs px-1.5 py-0.5 rounded bg-danger/15 text-danger"
    >
      fail
    </span>
  );
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'medium' });
  } catch {
    return iso;
  }
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="text-left px-3 py-2 font-medium">{children}</th>;
}

function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={cn('px-3 py-2 align-top', className)}>{children}</td>;
}
